"use client";

import {
  cinematicModalBackdropClass,
  cinematicModalCloseButtonClass,
  cinematicModalMaxWidthClass,
  cinematicModalPanelClass,
} from "@/lib/cinematicModalStyles";
import { cn } from "@/lib/utils";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { type RefObject, useRef } from "react";

type CinematicModalMaxWidth = keyof typeof cinematicModalMaxWidthClass;

interface CinematicModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: CinematicModalMaxWidth;
  panelClassName?: string;
  contentClassName?: string;
  closeButtonClassName?: string;
  showCloseButton?: boolean;
  titleId?: string;
  ariaLabel?: string;
  /** Retained for compatibility. Cinematic modals are always portaled for reliable positioning. */
  usePortal?: boolean;
  zIndexClassName?: string;
  dismissOnBackdropClick?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  fallbackFocusRef?: RefObject<HTMLElement | null>;
}

export function CinematicModal({
  isOpen,
  onClose,
  children,
  maxWidth = "md",
  panelClassName,
  contentClassName,
  closeButtonClassName,
  showCloseButton = true,
  titleId,
  ariaLabel,
  zIndexClassName,
  dismissOnBackdropClick = false,
  initialFocusRef,
  fallbackFocusRef,
}: CinematicModalProps) {
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(cinematicModalBackdropClass, zIndexClassName)}
          data-cinematic-modal-backdrop=""
        />
        <Dialog.Content
          aria-label={ariaLabel}
          aria-labelledby={titleId}
          aria-describedby={undefined}
          className={cn(
            cinematicModalPanelClass,
            cinematicModalMaxWidthClass[maxWidth],
            zIndexClassName,
            panelClassName
          )}
          onOpenAutoFocus={(event) => {
            previouslyFocusedElementRef.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
            if (initialFocusRef?.current) {
              event.preventDefault();
              initialFocusRef.current.focus();
            }
          }}
          onPointerDownOutside={(event) => {
            if (!dismissOnBackdropClick) event.preventDefault();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const opener = previouslyFocusedElementRef.current;
            if (opener?.isConnected) opener.focus();
            else fallbackFocusRef?.current?.focus();
          }}
        >
          {showCloseButton && (
            <Dialog.Close asChild>
              <button
                type="button"
                className={cn(
                  cinematicModalCloseButtonClass,
                  "absolute right-3 top-3 z-10",
                  closeButtonClassName
                )}
                aria-label="Close"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </Dialog.Close>
          )}
          <div
            className={cn(
              "min-h-0 overflow-y-auto overscroll-contain p-5 sm:p-6",
              contentClassName
            )}
            data-cinematic-modal-content=""
          >
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
